<template>
  <v-col
    ref="participantCard"
    class="participant-video"
    :cols="cols"
    :class="parentClasses"
    v-observe-visibility="visibilityChanged"
    v-resize="checkElementWidth"
  >
    <div class="item-card" @click="toggleSelect" :class="classes">
      <participant-role-badge :isHost="isParticipantHost" />

      <participant-assignment
        v-if="!isParticipantHost"
        :status="participant.assignment_status"
        :order="participant.assignment_order"
      />

      <v-icon
        class="item-card__check"
        v-if="selectable"
        :color="participant.selected ? 'primary' : null"
      >
        check_circle
      </v-icon>

      <div
        class="video-wrapper"
        :class="{ 'no-video': !videoTrackActive }"
        ref="video"
      >
        <span class="av-off pa-2" v-if="!isParticipantHost">
          Waiting for Audio/Video
        </span>

        <div class="avatar-wrap" v-if="!videoTrackActive && participant.avatar">
          <avatar :img="participant.avatar" />
        </div>
      </div>

      <participant-debug-info :participant="participant" />

      <content-line>
        <template v-slot:text>
          <participant-name
            :participantName="participantName"
            :info="participant.info"
            :isVisible="isVisible"
          />

          <participant-info
            v-if="participant.info"
            :participantInfo="participant.info"
            :isVisible="isVisible"
          />

          <div
            v-if="showTimeOfJoin"
            class="content-line__text-main content-line__text--label"
          >
            {{ timeOfJoin }}
          </div>
        </template>

        <template v-slot:actions>
          <participant-controls
            :participant="participant"
            :activeTracks="activeTracks"
            :small="isSmall"
            @sendCustomAction="$emit('sendCustomAction', $event)"
          />
        </template>
      </content-line>
    </div>
  </v-col>
</template>

<script>
import { eventBus } from '@/lib/events';
import { mapGetters, mapState } from 'vuex';
import { format } from 'date-fns';

import Avatar from '@/components/basic/avatar/Avatar.vue';
import ParticipantRoleBadge from './ParticipantRoleBadge.vue';
import ParticipantAssignment from './ParticipantAssignment.vue';
import ParticipantDebugInfo from './ParticipantDebugInfo.vue';
import ParticipantName from './ParticipantName.vue';
import ParticipantInfo from './ParticipantInfo.vue';
import ParticipantControls from './ParticipantControls.vue';

export default {
  name: 'RoomParticipantVideo',
  components: {
    Avatar,
    ParticipantAssignment,
    ParticipantRoleBadge,
    ParticipantDebugInfo,
    ParticipantName,
    ParticipantInfo,
    ParticipantControls,
  },
  props: {
    cols: {
      type: Number,
      default: 12,
    },
    participant: {
      type: Object,
      default: () => ({}),
    },
    audioOutputDeviceId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isVisible: false,
      isSmall: false,
      activeTracks: {
        audio: false,
        video: false,
      },
    };
  },

  computed: {
    ...mapState('rooms', ['roomInfo']),
    ...mapGetters('rooms', ['isHost', 'getParticipantJoinInfo']),
    videoTrackActive() {
      return this.activeTracks?.video || false;
    },

    participantJoinRecord() {
      return this.getParticipantJoinInfo(this.participantId);
    },

    showTimeOfJoin() {
      return !this.isParticipantHost && !!this.timeOfJoin;
    },

    timeOfJoin() {
      if (!this.participantJoinRecord) return null;
      const { date } = this.participantJoinRecord;
      const formattedTime = format(new Date(date), 'h:mm aaa');
      if (this.isSmall) {
        return `Joined ${formattedTime}`;
      }
      return `Joined at ${formattedTime}`;
    },

    participantId() {
      return this.participant.identity;
    },

    muted() {
      return this.participant.muted;
    },

    videoOff() {
      return this.participant.video_off;
    },

    parentClasses() {
      return {
        hidden: this.hidden,
      };
    },

    hidden() {
      return this.participant.hidden && !this.isParticipantHost;
    },

    showSelection() {
      return this.participant.showSelection;
    },

    selectable() {
      return (
        this.isHost &&
        !this.isParticipantHost &&
        !this.hidden &&
        this.showSelection
      );
    },

    classes() {
      let selectable = this.selectable;
      let part = this.participant;

      return {
        selectable,
        selected: selectable && part.selected && this.showSelection,
      };
    },

    isParticipantHost() {
      return this.participantId.includes('host');
    },

    participantName() {
      const { name, identity } = this.participant || {};
      const nameFromMessages = this.participantJoinRecord?.name;
      return name || nameFromMessages || identity;
    },
  },

  watch: {
    audioOutputDeviceId(deviceId) {
      let audio = this.getAudio();
      if (typeof audio?.setSinkId === 'function') {
        audio.setSinkId(deviceId);
      }
    },

    videoOff() {
      this.attachExistingTracks(['video']);
    },

    muted() {
      this.attachExistingTracks(['audio']);
    },

    participant: {
      deep: true,
      handler() {
        this.addParticipantEventListeners();
      },
    },
  },

  methods: {
    checkElementWidth() {
      const el = this.$refs.participantCard;
      if (!el) return;

      const { width } = el.getBoundingClientRect();
      this.isSmall = width < 290;
    },

    updateInfo(info = {}, options = {}) {
      info.identity = info.identity || this.participant.identity;
      eventBus.trigger('participantInfoUpdated', {
        info,
        options,
      });
    },

    isScreenShareTrack(track) {
      const { name = '' } = track || {};
      return name.endsWith('_sa') || name.endsWith('_sv');
    },

    async attachTrack(track) {
      let video = this.$refs.video;
      if (!video || this.isScreenShareTrack(track)) return;

      this.removeTrackElementByKind(track);
      await this.$nextTick();

      let kind = track.kind;

      //do not attach audio track if participant is muted.
      if (kind === 'audio' && this.muted) return;

      //do not attach video track if participant video is off.
      if (kind === 'video' && this.videoOff) return;

      //check to see if the track variable is really a track
      //publication and is holding the true track
      track = track.track ? track.track : track;
      let trackElement = track.attach();
      this.activeTracks[track.kind] = true;

      video.appendChild(trackElement);
    },

    /**
     * note that participant.tracks are trackPublication
     * classes not new Track classes. They hold the tracks
     * @return {[type]} [description]
     */
    attachExistingTracks(kinds = []) {
      let part = this.participant;

      if (!part.tracks) {
        return false;
      }

      part.tracks.forEach((track) => {
        if (track.kind === 'data') return;

        if (kinds.length && !kinds.includes(track.kind)) return;

        if (track.isSubscribed) {
          this.attachTrack(track);
        }
      });
    },

    removeTrackElementByKind(track) {
      const { kind = '' } = track || {};
      if (
        !this.$refs.video ||
        kind === 'data' ||
        this.isScreenShareTrack(track)
      )
        return;

      let method = kind === 'video' ? 'getVideo' : 'getAudio';
      let el = this[method]();
      if (el) {
        this.activeTracks[kind] = false;
        //pausing first before removing because Safari bug
        // https://vimeo.com/711428982/c93da21138
        el.pause();
        //then remove
        el.remove();
      }
    },

    getTrackElement(type) {
      let container = this.$refs.video;
      if (!container) return;

      return container.querySelector(type);
    },

    getVideo() {
      return this.getTrackElement('video');
    },

    getAudio() {
      return this.getTrackElement('audio');
    },

    toggleSelect() {
      if (!this.isHost || !this.selectable || this.isParticipantHost) {
        return;
      }
      this.updateInfo({
        selected: !this.participant.selected,
      });
    },

    visibilityChanged(isVisible) {
      this.isVisible = isVisible;

      //just updating video / pause play for hosts right now
      if (!this.isHost) return;

      let video = this.getVideo();
      if (video) {
        if (isVisible) {
          video.play();
        } else {
          video.pause();
        }
      }
    },

    addParticipantEventListeners() {
      if (!this.participant?.on) return;

      this.participant.on('trackUnsubscribed', (track) => {
        this.removeTrackElementByKind(track);
      });
    },
  },

  created() {
    this.$nextTick(() => {
      this.updateInfo({ element: this });
      this.attachExistingTracks();
      this.addParticipantEventListeners();
    });
  },
};
</script>

<style lang="less">
.participant-video {
  opacity: 1;
  transform: scale(1);
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

  .content-line {
    height: 60px;
  }

  &.hidden {
    opacity: 0.2;
    transform: scale(0.5);
  }

  .video-wrapper {
    background-size: cover;
    background-repeat: no-repeat;
    background-position: 50% 50%;

    &.blocked {
      position: relative;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background: rgba(0, 0, 0, 0.8)
          url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M1 0 L0 1 L99 100 L100 99' fill='teal' /></svg>");
        background-repeat: no-repeat;
        background-position: center center;
        background-size: 102% 102%, auto;
        z-index: 1;
      }
    }

    // Fix video height bug
    video {
      height: calc(100% + 1px);
    }

    &.no-video {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #000;

      .avatar-wrap {
        position: absolute;
        top: 0;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        cursor: initial;

        .v-avatar {
          height: auto !important;
          width: 40% !important;
          max-width: 300px;
          position: relative;
          overflow: hidden;
          border-radius: 50%;

          &:after {
            content: '';
            display: block;
            padding-bottom: 100%;
          }
        }

        .v-avatar img {
          display: block;
          height: 100%;
          width: 100%;
          position: absolute;
          top: 0;
          left: 0;
        }
      }
    }
  }

  .item-card {
    overflow: visible;
    &.selected {
      border: 3px solid var(--v-primary-base);
    }
  }

  .av-off {
    position: absolute;
    bottom: 0;
    right: 0;
    color: rgba(255, 255, 255, 0.8);
  }

  .content-line__text--label {
    font-size: 11px;
  }
}

.participant-type {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  color: #fff;
  padding: 5px 10px;
  border-radius: 0 0 0 4px;
  box-shadow: @base-shadow;
  display: flex;
  align-items: center;
  font-size: 12px;
  user-select: none;

  .v-icon {
    font-size: 12px;
    margin-right: 6px;
  }
}

.participant-debug-info {
  position: absolute;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-family: courier;
  bottom: 57px;
  padding-left: 4px;
  padding-right: 4px;
  left: 0;
  z-index: 1;
  font-size: 12px;
}
</style>
